import { GetRowIdParams, SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AxiosError } from 'axios';
import { ChangeEvent, FC, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useQueryClient } from 'react-query';
import { useLocation, useParams } from 'react-router-dom';
import DeleteConfirmationModal from '../../../common/DeleteConfirmationModal/DeleteConfirmationModal';
import {
  DropdownWrapperMedium,
  InputWrapperLarge,
  InputWrapperMedium,
} from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import { TableWrapper } from '../../../common/SideBySideTableLayout/SideBySideTableLayout.styles';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import { Variant } from '../../../components/Button/Button.types';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import { EditCellRenderer } from '../../../components/Table/EditCellRenderer/EditCellRenderer';
import Table from '../../../components/Table/Table';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { STATUS, StatusKeys } from '../../../constants/ApiEnums';
import { useSpec } from '../../../services/spec/spec.service';
import {
  useDeleteSubspec,
  useEditSubrecipe,
  useSubrecipeDetails,
  useSubrecipesInSpec,
} from '../../../services/subrecipe/subrecipe.service';
import { Subrecipe, SubrecipeDetails } from '../../../services/subrecipe/subrecipe.types';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import {
  genericErrorShowToastOptions,
  useGenericErrorMessage,
} from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import AddOption from '../AddOption/AddOption';
import AddOrChangeRawProd from '../AddOrChangeRawProd/AddOrChangeRawProd';
import { OptionRecipeForm, OptionRecipeInfo, Row, SaveButton, TableRow } from './EditOptionRecipe.styles';
import { EditOptionRecipeFormValues } from './EditOptionRecipe.types';

const EditOptionRecipe: FC = () => {
  const { specNo } = useParams();
  const _specNo = (specNo as string) || '';
  const location = useLocation();
  const gridRef = useRef<AgGridReact>(null);
  const optionTableRef = useRef<AgGridReact>(null);
  const queryClient = useQueryClient();

  const [formValues, setFormValues] = useState<EditOptionRecipeFormValues>({
    statusNo: '',
    description: '',
    longDescription: '',
  });

  const [initialFormValues, setInitialFormValues] = useState<EditOptionRecipeFormValues>({
    statusNo: '',
    description: '',
    longDescription: '',
  });

  const [addRawProdsActive, setAddRawProdsActive] = useState<boolean>(false);
  const [addOptionActive, setAddOptionActive] = useState<boolean>(false);
  const [changeRawProd, setChangeRawProd] = useState<boolean>(false);
  const [unsavedChangesExist, setUnsavedChangesExist] = useState<boolean>(false);
  const [selectedSubrecipe, setSelectedSubrecipe] = useState<Subrecipe | undefined>(undefined);
  const [selectedOption, setSelectedOption] = useState<SubrecipeDetails | undefined>(undefined);
  const [optionsTableData, setOptionsTableData] = useState<SubrecipeDetails[]>([]);

  const { data: specResponse, isError: specError, refetch: refetchSpec } = useSpec({ specNo: _specNo }, true);
  const { data: subrecipesResponse, isError: subrecipesError } = useSubrecipesInSpec({ specNo: _specNo }, true);
  const {
    data: subrecipeDetailsResponse,
    remove: removeSubrecipeDetails,
    isError: subrecipeDetailsError,
    refetch,
  } = useSubrecipeDetails(
    {
      specNo: _specNo,
      classNo: (selectedSubrecipe as Subrecipe)?.classNo,
      baseNo: (selectedSubrecipe as Subrecipe)?.baseNo,
      sizeNo: (selectedSubrecipe as Subrecipe)?.sizeNo,
      productNo: (selectedSubrecipe as Subrecipe)?.prodNo,
    },
    selectedSubrecipe !== undefined,
  );
  const { mutate, isError, isLoading: isSaveLoading, error } = useEditSubrecipe(_specNo);

  const specData = specResponse?.data;
  const subrecipesData: Subrecipe[] = subrecipesResponse?.data || [];
  const subrecipeDetailsData: SubrecipeDetails[] = subrecipeDetailsResponse?.data || [];

  subrecipesData?.forEach((element) => {
    element.classNoDesc = element.classNo + ' - ' + element.classDesc;
    element.baseNoDesc = element.baseNo + ' - ' + element.baseDesc;
    element.sizeNoDesc = element.sizeNo + ' - ' + element.sizeDesc;
    element.prodNoDesc = element.prodNo + ' - ' + element.prodDesc;
  });

  subrecipeDetailsData?.forEach((element) => {
    element.optionNoDesc = element.optionNo + ' - ' + element.optionDesc;
    element.rawProdNoDesc = element.rawProdNo + ' - ' + element.rawProdDesc;
  });
  useFetchErrorToast([
    { isError: specError, id: 'useSpec', label: 'Error fetching spec details' },
    { isError: subrecipesError, id: 'useSubrecipesInSpec', label: 'Error fetching subrecipes for this spec' },
    { isError: subrecipeDetailsError, id: 'useSubrecipeDetails', label: 'Error fetching options for this subrecipe' },
  ]);

  useGenericErrorMessage([{ isError, error }]);

  useEffect(() => {
    if (subrecipeDetailsData.length > 0) setOptionsTableData(subrecipeDetailsData);
  }, [subrecipeDetailsData]);

  useEffect(() => {
    if (subrecipesData.length > 0 && gridRef.current?.api && location.state) {
      // preselect option that as clicked when this page was linked to
      const { classNo, baseNo, sizeNo, prodNo } = location.state as any;
      if (classNo !== undefined && baseNo !== undefined && sizeNo !== undefined && prodNo !== undefined) {
        gridRef.current?.api.forEachNode((row) => {
          if (
            row.data.classNo === classNo &&
            row.data.baseNo === baseNo &&
            row.data.sizeNo === sizeNo &&
            row.data.prodNo === prodNo
          ) {
            row.setSelected(true);
            location.state = {};
          }
        });
      }
    }
  }, [subrecipesData, gridRef.current?.api]);

  useEffect(() => {
    if (specData) {
      setFormValues({
        statusNo: specData.statusNo?.toString(),
        description: specData.description,
        longDescription: specData.longDescription,
      });
      setInitialFormValues({
        statusNo: specData.statusNo?.toString(),
        description: specData.description,
        longDescription: specData.longDescription,
      });
    }
  }, [specData]);

  const handleAddRawProdClick = () => {
    setAddRawProdsActive(true);
  };

  const handleChangeRawProdClick = () => {
    setAddRawProdsActive(true);
    setChangeRawProd(true);
  };

  const handleAddOptionClick = () => {
    setAddOptionActive(true);
  };

  const [deleteConfirmationModalVisible, setDeleteConfirmationModalVisible] = useState(false);

  const closeDeleteConfirmationModal = () => {
    setDeleteConfirmationModalVisible(false);
  };

  const handleRemoveClick = () => {
    setDeleteConfirmationModalVisible(true);
  };

  const { mutate: deleteSubspec, isLoading: isDeleteLoading } = useDeleteSubspec();

  const handleDeleteSubspec = () => {
    if (selectedOption) {
      deleteSubspec(
        {
          specNo: selectedOption.specNo,
          subSpecNo: selectedOption.subSpecNo,
          rawProdNo: selectedOption.rawProdNo,
        },
        {
          onSuccess: () => {
            closeDeleteConfirmationModal();
            queryClient.invalidateQueries('subrecipe-details');
            showToast({
              id: 'deleteSubspecSuccess',
              label: 'The selected Option has been successfully deleted.',
              toastType: ToastTypes.Success,
            });
          },
          onError: (error) => {
            if ((error as AxiosError).message === 'Request failed with status code 409') {
              showToast({
                id: 'deleteSubspec409',
                label: 'The selected subspec is unique and was not deleted.',
                toastType: ToastTypes.Error,
              });
            } else if ((error as AxiosError).message === 'apiGatewayTimeout') {
            } else {
              showToast({ ...genericErrorShowToastOptions, id: 'deleteSubspecOtherError' });
            }
            closeDeleteConfirmationModal();
          },
        },
      );
    }
  };

  const handleSaveClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    mutate(
      {
        specNo: +_specNo,
        statusNo: +formValues.statusNo,
        description: formValues.description,
        longDescription: formValues.longDescription,
        subRecipeDetailDtoList: optionsTableData,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([['sub-recipes', specNo], ['subrecipe-details']]);
          refetch();
          refetchSpec();
          showToast({
            label: 'Option recipe has been updated',
            id: 'subrecipeUpdateSuccess',
          });
          setInitialFormValues({ ...formValues });
          setUnsavedChangesExist(false);
        },
      },
    );
  };

  const handleSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleSelectionChangedSubrecipe = (event: SelectionChangedEvent) => {
    const selectedRow = event.api.getSelectedRows()[0];
    if (!selectedRow) {
      removeSubrecipeDetails();
      setOptionsTableData([]);
    }
    setSelectedSubrecipe(selectedRow);
  };

  const selectAvailableSubrecipes = () => {    if (selectedSubrecipe) {
      gridRef?.current?.api?.forEachNode((node) => {
        if (
          selectedSubrecipe.classNo === node.data.classNo &&
          selectedSubrecipe.baseNo === node.data.baseNo &&
          selectedSubrecipe.sizeNo === node.data.sizeNo &&
          selectedSubrecipe.prodNo === node.data.prodNo
        ) {
          node.setSelected(true);
        }
      });
    }    if (selectedOption) {
      optionTableRef?.current?.api?.forEachNode((node) => {
        if ((selectedOption.subSpecNo === node?.data.subSpecNo) && (selectedOption.optionNo === node?.data.optionNo) && (selectedOption.rawProdNo === node?.data.rawProdNo)) {
          node.setSelected(true);
        }
      });
    }
  };

  const getRowId = (params: GetRowIdParams) => {
    const rowId = params.data.subSpecNo + '-' + params.data.optionNo + '-' + params.data.rawProdNo;
    return rowId;
  };
  useEffect(() => {
    if (selectedSubrecipe) {
      selectAvailableSubrecipes();
    }
  });

  const handleSelectionChangedOption = (event: SelectionChangedEvent) => {
    setSelectedOption(event.api.getSelectedRows()[0]);
  };

  const checkForUnsavedChanges = () => {
    // checked if formValues have changed
    if (
      formValues.description !== initialFormValues.description ||
      formValues.longDescription !== initialFormValues.longDescription ||
      formValues.statusNo !== initialFormValues.statusNo
    ) {
      setUnsavedChangesExist(true);
      return;
    }
    // check if usage amounts have changed
    if (!isEqual(subrecipeDetailsData, optionsTableData)) {
      setUnsavedChangesExist(true);
      return;
    }
    // if we haven't hit a return statement, no unsaved changes exist
    setUnsavedChangesExist(false);
  };

  useEffect(() => {
    checkForUnsavedChanges();
  }, [formValues, optionsTableData]);

  const handleCellEdit = useCallback(    (event: any) => {
      let newValue = +event.newValue;
      if (newValue > 9999.9999) newValue = 9999.9999;
      if (newValue < -9999.9999) newValue = -9999.9999;
      if (Number.isNaN(newValue)) newValue = event.oldValue;

      const newOptionsTableData = optionsTableData.map((detail) => {
        if (detail.subSpecNo === event.data.subSpecNo && detail.rawProdNo === event.data.rawProdNo) {
          return { ...detail, usageAmount: newValue };
        } else {
          return detail;
        }
      });

      setOptionsTableData(newOptionsTableData);
    },
    [optionsTableData],
  );

  return addRawProdsActive ? (
    <AddOrChangeRawProd
      selectedOption={selectedOption}
      subrecipeDetailsData={subrecipeDetailsData}
      changeRawProd={changeRawProd}
      setChangeRawProd={setChangeRawProd}
      setAddRawProdsActive={setAddRawProdsActive}
      setOptionsTableData={setOptionsTableData}
      setSelectedOption={setSelectedOption}
      setSelectedSubrecipe={setSelectedSubrecipe}
      removeSubrecipeDetails={removeSubrecipeDetails}
      refetch={refetch}
    />
  ) : addOptionActive ? (
    <AddOption specNo={_specNo} setAddOptionActive={setAddOptionActive} />
  ) : (
    <>
      <Header breadcrumb={{ to: '/optionrecipes', label: 'Back to All Recipes' }} title={`Spec ${specNo}`} />
      <OptionRecipeForm>
        <H2>Option Recipe Info</H2>
        <OptionRecipeInfo>
          <Row>
            <InputWrapperMedium>
              <Input label="Spec No" id="specNo" disabled value={_specNo} />
            </InputWrapperMedium>
            <InputWrapperLarge>
              <Input label="Description" id="description" value={formValues.description} onChange={handleChange} />
            </InputWrapperLarge>
            <InputWrapperLarge>
              <Input
                label="Long Description"
                id="longDescription"
                value={formValues.longDescription}
                onChange={handleChange}
              />
            </InputWrapperLarge>
          </Row>
          <Row>
            <DropdownWrapperMedium>
              <Dropdown
                label="Status"
                id="statusNo"
                options={Object.keys(STATUS).map((key: any) => {
                  return { value: key, label: `${key} - ${STATUS[key as StatusKeys]}` };
                })}
                value={formValues?.statusNo?.toString()}
                onChange={handleSelect}
              />
            </DropdownWrapperMedium>
            <InputWrapperLarge>
              <Input
                label="Last Update"
                id="updateDate"
                disabled
                value={
                  specData
                    ? new Date(specData?.updatedDate || '')?.toLocaleDateString() +
                    ' ' +
                    new Date(specData?.updatedDate || '')?.toLocaleTimeString()
                    : ''
                }
              />
            </InputWrapperLarge>
            <InputWrapperLarge>
              <Input label="Update By" id="updateBy" disabled value={specData?.updatedBy} />
            </InputWrapperLarge>
          </Row>
        </OptionRecipeInfo>
        <TableRow>
          <TableWrapper>
            <Table
              showHeader
              headerTitle="SubRecipe Details"
              height="800px"
              rowData={subrecipesData}
              returnedRecords={subrecipesData?.length}
              rowSelection="single"
              onSelectionChanged={handleSelectionChangedSubrecipe}
              ref={gridRef}
              columnDefs={[
                {
                  width: 48,
                  field: '',
                  headerName: '',
                  checkboxSelection: true,
                  floatingFilter: false,
                  sortable: false,
                },
                { minWidth: 48, field: 'classNoDesc', headerName: 'Class' },
                { minWidth: 48, field: 'baseNoDesc', headerName: 'Base' },
                { minWidth: 48, field: 'sizeNoDesc', headerName: 'Size' },
                { minWidth: 48, field: 'prodNoDesc', headerName: 'Prod', flex: 1, resizable: false },
              ]}
            />
          </TableWrapper>
          <TableWrapper>
            <Table
              showHeader
              headerTitle="Options"
              headerButtons={[{
                label: 'Add Option',
                variant: Variant.Secondary,
                onClick: handleAddOptionClick,
                testId: 'AddOptionButton',
              }]}
              footerButtons={[
                {
                  label: 'Add Raw Prod',
                  variant: Variant.Secondary,
                  testId: 'addRawProductButton',
                  onClick: handleAddRawProdClick,
                  disabled: selectedOption === undefined,
                },
                {
                  label: 'Change Raw Prod',
                  variant: Variant.Secondary,
                  onClick: handleChangeRawProdClick,
                  disabled: selectedOption === undefined,
                },
                {
                  label: 'Remove',
                  variant: Variant.Secondary,
                  onClick: handleRemoveClick,
                  disabled: selectedOption === undefined,
                },
              ]}
              height="714px"
              columnDefs={[
                {
                  width: 48,
                  field: '',
                  headerName: '',
                  checkboxSelection: true,
                  floatingFilter: false,
                  sortable: false,
                  flex: 0.25,
                },
                {
                  minWidth: 48,
                  field: 'subSpecNo',
                  headerName: 'Subspec',
                  flex: 0.75,
                  tooltipValueGetter: (params) => params.value,
                },
                {
                  minWidth: 48,
                  field: 'optionNoDesc',
                  headerName: 'Option',
                  tooltipValueGetter: (params) => params.value,
                  flex: 1,
                },
                {
                  minWidth: 48,
                  field: 'rawProdNoDesc',
                  headerName: 'Raw Product',
                  tooltipValueGetter: (params) => params.value,
                  flex: 1.50,
                },
                {
                  minWidth: 48,
                  field: 'usageAmount',
                  headerName: 'Usage Amt',
                  resizable: false,
                  flex: 0.75,
                  editable: true,
                  cellRenderer: EditCellRenderer,
                },
              ]}
              rowData={optionsTableData}
              ref={optionTableRef}
              returnedRecords={optionsTableData.length}
              rowSelection="single"
              suppressRowClickSelection={true}
              tooltipShowDelay={0}
              isSizeColumnsToFit={false}
              onSelectionChanged={handleSelectionChangedOption}
              readOnlyEdit={true}
              onCellEditRequest={handleCellEdit}
              getRowId={getRowId}
            />
          </TableWrapper>
        </TableRow>
        <SaveButton label="Save" onClick={handleSaveClick} disabled={!unsavedChangesExist} isLoading={isSaveLoading} />
      </OptionRecipeForm>
      <UnsavedChangesModal unsavedChangesExist={unsavedChangesExist} />
      <DeleteConfirmationModal
        open={deleteConfirmationModalVisible}
        onClose={closeDeleteConfirmationModal}
        onDelete={handleDeleteSubspec}
        isLoading={isDeleteLoading}
      />
    </>
  );
};

export default EditOptionRecipe;
